import React, { useEffect, useState } from 'react';
import { Wallet, Loader2, Plus, Check } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import DatePicker from '@/components/ui/DatePicker';
import { CostRecurrence, ShopCost } from '@/services/shop/shopCostsService';

const inputCls = 'w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none font-medium transition-all placeholder:text-gray-400 text-gray-900';
const labelCls = 'block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2';

export const RECURRENCE_OPTS: { value: CostRecurrence; label: string }[] = [
    { value: 'monthly', label: 'Mensile' },
    { value: 'quarterly', label: 'Trimestrale' },
    { value: 'yearly', label: 'Annuale' },
    { value: 'custom_days', label: 'Ogni N giorni' },
    { value: 'one_off', label: 'Una tantum' },
];

export interface CostPayload {
    name: string;
    category: string | null;
    amount: number;
    recurrence: CostRecurrence;
    interval_days: number | null;
    start_date: string | null;
    note: string | null;
}

interface CostFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (payload: CostPayload) => void | Promise<void>;
    saving?: boolean;
    /** Se presente, il form è in modifica e viene precompilato con questi valori. */
    initial?: ShopCost | null;
}

const toLocalIso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Creazione/modifica di un costo del salone (affitto, utenze, forniture, …).
 * Riusa lo stesso form per entrambi i casi: `initial` presente ⇒ modalità modifica.
 */
const CostFormModal: React.FC<CostFormModalProps> = ({ isOpen, onClose, onSubmit, saving = false, initial = null }) => {
    const isEdit = !!initial;
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [amount, setAmount] = useState('');
    const [recurrence, setRecurrence] = useState<CostRecurrence>('monthly');
    const [intervalDays, setIntervalDays] = useState('');
    const [startDate, setStartDate] = useState(''); // ISO yyyy-mm-dd
    const [note, setNote] = useState('');

    // Reset (o precompilazione in modifica) a ogni apertura.
    useEffect(() => {
        if (!isOpen) return;
        setName(initial?.name ?? '');
        setCategory(initial?.category ?? '');
        setAmount(initial ? String(initial.amount) : '');
        setRecurrence(initial?.recurrence ?? 'monthly');
        setIntervalDays(initial?.interval_days ? String(initial.interval_days) : '');
        setStartDate(initial?.start_date ?? '');
        setNote(initial?.note ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const amountNum = parseFloat(amount);
    const intervalOk = recurrence !== 'custom_days' || (!!parseInt(intervalDays) && parseInt(intervalDays) > 0);
    const canSubmit = name.trim().length > 0 && !!amountNum && amountNum > 0 && intervalOk && !saving;

    const submit = () => {
        if (!canSubmit) return;
        onSubmit({
            name: name.trim(),
            category: category.trim() || null,
            amount: amountNum,
            recurrence,
            interval_days: recurrence === 'custom_days' ? parseInt(intervalDays) : null,
            start_date: startDate || null,
            note: note.trim() || null,
        });
    };

    const startDateObj = startDate ? new Date(`${startDate}T00:00:00`) : undefined;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-xl"
            headerClassName="bg-white border-b border-gray-100"
            headerContent={
                <div className="flex items-center gap-3 pr-8">
                    <div className="w-11 h-11 rounded-2xl bg-clip-50 text-clip-600 flex items-center justify-center">
                        <Wallet size={20} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{isEdit ? 'Modifica costo' : 'Nuovo costo'}</h2>
                        <p className="text-sm text-gray-400">Affitto, utenze, forniture e altre spese fisse.</p>
                    </div>
                </div>
            }
        >
            <div className="space-y-5">
                {/* Nome */}
                <div>
                    <label className={labelCls}>Nome</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Affitto" className={inputCls} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Categoria */}
                    <div>
                        <label className={labelCls}>Categoria (facoltativa)</label>
                        <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="es. Utenze" className={inputCls} />
                    </div>
                    {/* Importo */}
                    <div>
                        <label className={labelCls}>Importo (€)</label>
                        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="es. 900" className={inputCls} />
                    </div>
                    {/* Ricorrenza */}
                    <div>
                        <label className={labelCls}>Ricorrenza</label>
                        <select value={recurrence} onChange={(e) => setRecurrence(e.target.value as CostRecurrence)} className={inputCls}>
                            {RECURRENCE_OPTS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    {/* Ogni quanti giorni (solo custom) */}
                    {recurrence === 'custom_days' && (
                        <div>
                            <label className={labelCls}>Ogni quanti giorni</label>
                            <input type="number" value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} placeholder="es. 45" className={inputCls} />
                        </div>
                    )}
                    {/* Data inizio */}
                    <div>
                        <label className={labelCls}>Data inizio (facoltativa)</label>
                        <DatePicker
                            selected={startDateObj}
                            onSelect={(d) => setStartDate(d ? toLocalIso(d) : '')}
                            allowClosedDays
                            placeholder="Scegli una data"
                        />
                    </div>
                </div>

                {/* Note */}
                <div>
                    <label className={labelCls}>Note (facoltative)</label>
                    <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="es. contratto biennale" className={inputCls} />
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-bold text-sm transition-colors"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canSubmit}
                        className="flex items-center gap-2 px-5 py-2.5 bg-clip-500 hover:bg-clip-600 text-white font-bold rounded-xl text-sm transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : isEdit ? <Check size={16} /> : <Plus size={16} />} {isEdit ? 'Salva modifiche' : 'Aggiungi costo'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CostFormModal;
